import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { PageLoading } from "../components/ui/Feedback";
import { AuthProvider, useAuth } from "../core/auth/AuthProvider";

const PhoneLoginPage = lazy(() =>
  import("../features/auth/PhoneLoginPage").then((module) => ({
    default: module.PhoneLoginPage,
  })),
);
const OnboardingPage = lazy(() =>
  import("../features/auth/OnboardingPage").then((module) => ({
    default: module.OnboardingPage,
  })),
);
const ApprovalPendingPage = lazy(() =>
  import("../features/auth/ApprovalPendingPage").then((module) => ({
    default: module.ApprovalPendingPage,
  })),
);
const V3Workspace = lazy(() =>
  import("../features/v3/V3Workspace").then((module) => ({
    default: module.V3Workspace,
  })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AuthGate() {
  const { stage } = useAuth();
  if (stage === "booting") return <PageLoading />;
  if (stage === "anonymous")
    return (
      <Suspense fallback={<PageLoading />}>
        <PhoneLoginPage />
      </Suspense>
    );
  if (stage === "onboarding")
    return (
      <Suspense fallback={<PageLoading />}>
        <OnboardingPage />
      </Suspense>
    );
  if (stage === "pending")
    return (
      <Suspense fallback={<PageLoading />}>
        <ApprovalPendingPage />
      </Suspense>
    );

  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route path="/" element={<Navigate to="/campaigns" replace />} />
        <Route path="*" element={<V3Workspace />} />
      </Routes>
    </Suspense>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AuthGate />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
